import { Button, Tooltip } from "@may-i/mash-design-system";
import { forwardRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface ActionBarProps {
  onSave: () => void;
  onSaveAndAnalysis: () => void;
  isSaving?: boolean;
  /** 저장할 변경사항이 있는지 (F8). false 면 '저장' 버튼 비활성 + 우측 버튼 텍스트를 '분석 영역 설정'으로 */
  hasChanges?: boolean;
  /** 화각 조정 완료(committed) 안 된 카메라 라벨 배열 — 있으면 저장 차단 + tooltip 에 모두 나열 (PO QA 2차 j-1) */
  uncommittedCameraNames?: string[];
}

export const ActionBar = forwardRef<HTMLDivElement, ActionBarProps>(function ActionBar(
  { onSave, onSaveAndAnalysis, isSaving, hasChanges = true, uncommittedCameraNames },
  ref,
) {
  const { t } = useTranslation("camera-mapping");
  const hasUncommitted = (uncommittedCameraNames?.length ?? 0) > 0;
  const disabled = isSaving || hasUncommitted;
  // F8: 변경 없으면 '저장'은 의미 없으니 비활성. 우측 버튼은 활성 유지(저장 없이 다음 단계 진행 가능)
  const saveDisabled = disabled || !hasChanges;
  const reason = hasUncommitted
    ? t("action.disabledWhileAdjustingWithCamera", { cameras: uncommittedCameraNames!.join(", ") })
    : null;
  // disabled button 위에서 tooltip 이 보이려면 trigger 가 pointer events 를 받아야 함 → span 으로 감싸 hover 영역 확보
  const wrap = (children: ReactNode) =>
    reason ? (
      <Tooltip trigger={<HoverArea>{children}</HoverArea>} side="top" sideOffset={6}>
        {reason}
      </Tooltip>
    ) : (
      children
    );

  return (
    <Container ref={ref}>
      {wrap(
        <Button variant="outlined" color="primary" size="small" onClick={onSave} disabled={saveDisabled}>
          {t("action.save")}
        </Button>,
      )}
      {wrap(
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onSaveAndAnalysis}
          disabled={disabled}
        >
          {hasChanges ? t("action.saveAndAnalysis") : t("action.analysis")}
        </Button>,
      )}
    </Container>
  );
});

// --- Styles ---

const Container = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.12);
  border-radius: 8px 8px 0 0;
  z-index: 5;
`;

// disabled 버튼은 pointer events 를 받지 않아 tooltip 트리거가 안 됨 — 감싸는 span 으로 hover 영역 확보
const HoverArea = styled.span`
  display: inline-flex;
`;
